/**
 * The Sketch editor's unsaved state with undo and redo (pure): the drawn roof, the modules, the roof obstacles and
 * the shade sources around the house (trees, buildings, walls), and what a save or a preview sends. A drag is one
 * undo step (`transient` updates, then a `checkpoint`), as in the Designer.
 *
 * Dirty means "differs from the saved version" (as in editor/model.ts): each change compares what a save would send
 * (toSketchRoof, editorPanels, editorObstacles, editorShade) with the version loaded (`base`), so a click on the
 * gizmo that snaps nothing, a drag back to where it was, or an undo to the start is never an unsaved change.
 */
import type { S } from "../api/client";
import { draftFrom, editorObstacles, obstaclesSignature, panelsSignature, savedPanelsOf, settledPanels, type ObstacleItem, type PanelItem, type SavedPanels } from "../editor/model.ts";
import { fromRoofModel, toSketchRoof } from "./roofModel.ts";
import { editorShade, shadeFrom, type ShadeItem } from "./shade.ts";
import type { Sketch } from "./skeleton.ts";

export interface SketchDraft {
  sketch: Sketch;
  panels: PanelItem[];
  obstacles: ObstacleItem[];
  shade: ShadeItem[];
  /** Whether each part differs from the saved version (derived: `withSketchDirty`). */
  dirty: { roof: boolean; panels: boolean; obstacles: boolean; shade: boolean };
  /** The saved version, normalised (absent: nothing saved yet). */
  base?: SketchBase;
}

/** The loaded version in the form a save sends it. */
export interface SketchBase {
  roof: string;
  panels: string;
  /** The saved panels by id (a panel moved less than a millimetre is where it was: editor/model.ts settledPanels). */
  saved?: SavedPanels;
  obstacles: string;
  shade: string;
}

export const roofSignature = (sketch: Sketch) => JSON.stringify(toSketchRoof(sketch));
export const shadeSignature = (shade: ShadeItem[]) => JSON.stringify(editorShade(shade).map((o) => JSON.stringify(o)).sort());
const EMPTY_BASE: SketchBase = { roof: "", panels: "[]", obstacles: "[]", shade: "[]" };

/** The draft with `dirty` recomputed against the saved version (unchanged parts keep their flag). */
export function withSketchDirty(next: SketchDraft, prev?: SketchDraft): SketchDraft {
  const base = next.base ?? EMPTY_BASE;
  const keep = <K extends keyof SketchDraft>(k: K) => !!prev && next[k] === prev[k];
  const dirty = {
    roof: keep("sketch") ? prev!.dirty.roof : roofSignature(next.sketch) !== base.roof,
    panels: keep("panels") ? prev!.dirty.panels : panelsSignature(next.panels, base.saved) !== base.panels,
    obstacles: keep("obstacles") ? prev!.dirty.obstacles : obstaclesSignature(next.obstacles) !== base.obstacles,
    shade: keep("shade") ? prev!.dirty.shade : shadeSignature(next.shade) !== base.shade,
  };
  const d = next.dirty;
  const same = dirty.roof === d.roof && dirty.panels === d.panels && dirty.obstacles === d.obstacles && dirty.shade === d.shade;
  return same ? next : { ...next, dirty };
}

export interface SketchHistory {
  past: SketchDraft[];
  present: SketchDraft;
  future: SketchDraft[];
  /** The state before a drag in progress (transient updates). */
  pending: SketchDraft | null;
}

export type SketchAction =
  | { type: "sketch"; sketch: Sketch; transient?: boolean }
  | { type: "panels"; panels: PanelItem[]; transient?: boolean }
  | { type: "both"; sketch: Sketch; panels: PanelItem[] }
  | { type: "obstacles"; obstacles: ObstacleItem[]; transient?: boolean }
  | { type: "shade"; shade: ShadeItem[]; transient?: boolean }
  | { type: "checkpoint" }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "reset"; draft: SketchDraft };

const MAX_UNDO = 80;

export const CLEAN = { roof: false, panels: false, obstacles: false, shade: false };

export function sketchDraftFrom(doc: S["EditorDocument"]): SketchDraft {
  const loaded = draftFrom(doc);
  const draft: SketchDraft = { sketch: fromRoofModel(doc.roof_model), panels: loaded.panels, obstacles: loaded.obstacles, shade: shadeFrom(doc), dirty: CLEAN };
  const saved = savedPanelsOf(draft.panels);
  draft.base = { roof: roofSignature(draft.sketch), panels: panelsSignature(draft.panels, saved), saved, obstacles: obstaclesSignature(draft.obstacles), shade: shadeSignature(draft.shade) };
  return draft;
}

export function initialHistory(doc: S["EditorDocument"]): SketchHistory {
  return { past: [], present: sketchDraftFrom(doc), future: [], pending: null };
}

export function reduceSketch(h: SketchHistory, a: SketchAction): SketchHistory {
  const push = (next: SketchDraft, transient?: boolean): SketchHistory =>
    transient
      ? { ...h, present: next, pending: h.pending ?? h.present, future: [] }
      : { past: [...h.past, h.pending ?? h.present].slice(-MAX_UNDO), present: next, pending: null, future: [] };
  switch (a.type) {
    case "reset":
      return { past: [], present: a.draft, future: [], pending: null };
    case "sketch":
      return push(withSketchDirty({ ...h.present, sketch: a.sketch }, h.present), a.transient);
    case "panels":
      return push(withSketchDirty({ ...h.present, panels: a.panels }, h.present), a.transient);
    case "both":
      return push(withSketchDirty({ ...h.present, sketch: a.sketch, panels: a.panels }, h.present));
    case "obstacles":
      return push(withSketchDirty({ ...h.present, obstacles: a.obstacles }, h.present), a.transient);
    case "shade":
      return push(withSketchDirty({ ...h.present, shade: a.shade }, h.present), a.transient);
    case "checkpoint":
      return h.pending ? { ...h, past: [...h.past, h.pending].slice(-MAX_UNDO), pending: null } : h;
    case "undo": {
      const previous = h.past[h.past.length - 1];
      return previous ? { past: h.past.slice(0, -1), present: previous, pending: null, future: [...h.future, h.present].slice(-MAX_UNDO) } : h;
    }
    case "redo": {
      const next = h.future[h.future.length - 1];
      return next ? { past: [...h.past, h.present].slice(-MAX_UNDO), present: next, pending: null, future: h.future.slice(0, -1) } : h;
    }
  }
}

export const isSketchDirty = (d: SketchDraft) => d.dirty.roof || d.dirty.panels || d.dirty.obstacles || d.dirty.shade;

/** The body of `POST /designs/{id}/edits`: the drawn roof whenever it changed, and the modules (always with the
 * roof: faces it removed take their modules along); the obstacles and the shade sources when they changed (their
 * complete lists). */
export function sketchEditBody(base: number, d: SketchDraft, note: string): S["DesignEdit"] {
  const body: S["DesignEdit"] = { base_version: base, note: note.trim() || null };
  if (d.dirty.roof) body.sketch = toSketchRoof(d.sketch);
  if (d.dirty.roof || d.dirty.panels) {
    const faces = new Set(d.sketch.faces.map((f) => f.id));
    body.panels = settledPanels(d.panels.filter((p) => faces.has(p.face_id)), d.base?.saved);
  }
  if (d.dirty.obstacles) body.obstructions = editorObstacles(d.obstacles);
  if (d.dirty.shade) body.shade_objects = editorShade(d.shade);
  return body;
}

/** What a preview sends besides the roof and modules: the unsaved obstacles and shade sources, when changed. */
export function previewExtras(d: SketchDraft): Pick<S["EditorPreviewRequest"], "obstructions" | "shade_objects"> {
  return {
    ...(d.dirty.obstacles ? { obstructions: editorObstacles(d.obstacles) } : {}),
    ...(d.dirty.shade ? { shade_objects: editorShade(d.shade) } : {}),
  };
}

/** The production numbers of a Sketch without panels: nothing to show but zero (never the last preview's, e.g. the
 * "0.43 kW" of one module that stayed next to "No panels yet" once the roof or its panels were removed). */
export function noPanelNumbers(n: S["EditorNumbers"]): S["EditorNumbers"] {
  return { panel_count: 0, kw_dc: 0, annual_kwh: null, price: null, net_cost: null, annual_savings: null, payback_years: null, offset_pct: null, currency: n.currency ?? null, unshaded_annual_kwh: null, shading_loss_pct: null, warnings: [] };
}
